import { ReactNode, useCallback, useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface ProductUpdateAction {
  label: string;
  onClick: () => void;
}

interface ProductUpdateAnnouncementModalProps {
  enabled: boolean;
  announcementId: string;
  version: string;
  audienceScope: string[] | null;
  forceOpen?: boolean;
  onDismiss?: () => void;
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  dismissLabel?: string;
  closeLabel?: string;
  primaryAction?: ProductUpdateAction;
}

export default function ProductUpdateAnnouncementModal({
  enabled,
  announcementId,
  version,
  audienceScope,
  forceOpen = false,
  onDismiss,
  eyebrow = "New in DojoTrack",
  title,
  description,
  icon,
  children,
  dismissLabel = "Got it",
  closeLabel = "Close update announcement",
  primaryAction,
}: ProductUpdateAnnouncementModalProps) {
  const [open, setOpen] = useState(false);
  const storageKey = audienceScope?.length
    ? ["dojotrack", announcementId, version, ...audienceScope].join(":")
    : null;

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      return;
    }

    if (forceOpen) {
      setOpen(true);
      return;
    }

    if (!storageKey) {
      setOpen(false);
      return;
    }

    try {
      setOpen(localStorage.getItem(storageKey) !== "dismissed");
    } catch {
      // Some privacy modes disable storage. The announcement can still be shown.
      setOpen(true);
    }
  }, [enabled, forceOpen, storageKey]);

  const dismiss = useCallback(() => {
    if (!forceOpen && storageKey) {
      try {
        localStorage.setItem(storageKey, "dismissed");
      } catch {
        // Closing the modal should still work if storage is unavailable.
      }
    }
    setOpen(false);
    onDismiss?.();
  }, [forceOpen, onDismiss, storageKey]);

  const handlePrimaryAction = () => {
    dismiss();
    primaryAction?.onClick();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={dismiss} className="relative z-[10020]">
      <div
        className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="flex h-full items-center justify-center p-4 sm:p-6">
          <Dialog.Panel className="relative max-h-[calc(100dvh-3rem)] w-full max-w-3xl overflow-y-auto overscroll-contain rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
          <button
            type="button"
            onClick={dismiss}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-gray-500 shadow-sm transition hover:bg-white hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800/90 dark:text-gray-300 dark:hover:bg-gray-700"
            aria-label={closeLabel}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>

          <div className="flex-none border-b border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-blue-50 px-6 py-6 pr-16 dark:border-indigo-900 dark:from-indigo-950/60 dark:via-gray-800 dark:to-blue-950/40 sm:px-8 sm:py-7">
            <div className="flex items-start gap-4">
              {icon && (
                <div className="hidden rounded-xl bg-indigo-600 p-3 text-white shadow-sm sm:block">
                  {icon}
                </div>
              )}
              <div>
                {eyebrow && (
                  <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                    {eyebrow}
                  </p>
                )}
                <Dialog.Title
                  as="h2"
                  className="mt-1 text-2xl font-bold text-gray-900 dark:text-white"
                >
                  {title}
                </Dialog.Title>
                {description && (
                  <Dialog.Description className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                    {description}
                  </Dialog.Description>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6 px-6 py-6 sm:px-8">
            {children}
          </div>

          <div className="flex flex-none flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/50 sm:flex-row sm:justify-end sm:px-8">
            <button
              type="button"
              onClick={dismiss}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {dismissLabel}
            </button>
            {primaryAction && (
              <button
                type="button"
                onClick={handlePrimaryAction}
                className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                {primaryAction.label}
              </button>
            )}
          </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
